import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import ConfirmModal from '../UI/ConfirmModal';
import { useLang } from '../../i18n/LanguageContext';

import API_BASE_URL from '../../config';

const EMPTY_FORM = {
  code: '', discountType: 'percentage', discountValue: '', minOrderAmount: 0,
  maxUses: '', expiresAt: '', isActive: true
};

export default function CouponList({ showNotification }) {
  const { t } = useLang();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ open: false, id: null, code: '' });
  const [copiedCode, setCopiedCode] = useState(null);

  // Global Sales Tax & Discount settings
  const [settings, setSettings] = useState({ salesTaxType: 'fixed', salesTaxValue: 0, discountType: 'fixed', discountValue: 0 });
  const [savingSettings, setSavingSettings] = useState(false);

  const token = localStorage.getItem('ownerToken');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/coupons`, { headers });
      if (res.data.success) setCoupons(res.data.data);
    } catch {
      showNotification('Failed to load coupons', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/settings`);
      if (res.data.success && res.data.data) {
        const s = res.data.data;
        setSettings({
          salesTaxType: s.salesTaxType || 'fixed',
          salesTaxValue: s.salesTaxValue || 0,
          discountType: s.discountType || 'fixed',
          discountValue: s.discountValue || 0,
        });
      }
    } catch { /* non-fatal */ }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await axios.put(`${API_BASE_URL}/settings`, {
        salesTaxType: settings.salesTaxType,
        salesTaxValue: parseFloat(settings.salesTaxValue) || 0,
        discountType: settings.discountType,
        discountValue: parseFloat(settings.discountValue) || 0,
      }, { headers });
      if (res.data.success) showNotification(t('promotions.settingsSaved'), 'success');
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed to save settings', 'error');
    } finally {
      setSavingSettings(false);
    }
  };

  useEffect(() => { fetchCoupons(); fetchSettings(); }, []); // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code || !form.discountValue) return showNotification('Code and discount value are required', 'error');
    setFormLoading(true);
    try {
      const payload = {
        ...form,
        code: form.code.toUpperCase(),
        discountValue: parseFloat(form.discountValue),
        minOrderAmount: parseFloat(form.minOrderAmount) || 0,
        maxUses: form.maxUses ? parseInt(form.maxUses) : null,
        expiresAt: form.expiresAt || null,
      };
      let res;
      if (editId) {
        res = await axios.put(`${API_BASE_URL}/coupons/${editId}`, payload, { headers });
      } else {
        res = await axios.post(`${API_BASE_URL}/coupons`, payload, { headers });
      }
      if (res.data.success) {
        showNotification(editId ? 'Coupon updated!' : 'Coupon created!', 'success');
        setShowForm(false);
        setEditId(null);
        setForm(EMPTY_FORM);
        fetchCoupons();
      } else {
        showNotification(res.data.message || 'Failed', 'error');
      }
    } catch (err) {
      showNotification(err.response?.data?.message || 'Failed', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEdit = (coupon) => {
    setEditId(coupon._id);
    setForm({
      code: coupon.code,
      discountType: coupon.discountType,
      discountValue: coupon.discountValue,
      minOrderAmount: coupon.minOrderAmount || 0,
      maxUses: coupon.maxUses || '',
      expiresAt: coupon.expiresAt ? coupon.expiresAt.slice(0, 10) : '',
      isActive: coupon.isActive,
    });
    setShowForm(true);
  };

  const handleDelete = async () => {
    const { id } = confirmModal;
    setConfirmModal({ open: false, id: null, code: '' });
    try {
      const res = await axios.delete(`${API_BASE_URL}/coupons/${id}`, { headers });
      if (res.data.success) {
        setCoupons(prev => prev.filter(c => c._id !== id));
        showNotification('Coupon deleted', 'success');
      }
    } catch {
      showNotification('Failed to delete coupon', 'error');
    }
  };

  const handleToggleActive = async (coupon) => {
    try {
      const res = await axios.put(`${API_BASE_URL}/coupons/${coupon._id}`, { ...coupon, isActive: !coupon.isActive }, { headers });
      if (res.data.success) {
        setCoupons(prev => prev.map(c => c._id === coupon._id ? { ...c, isActive: !c.isActive } : c));
        showNotification(`Coupon ${!coupon.isActive ? 'activated' : 'deactivated'}`, 'success');
      }
    } catch {
      showNotification('Failed to update coupon', 'error');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    });
  };

  const isExpired = (coupon) => coupon.expiresAt && new Date(coupon.expiresAt) < new Date();

  return (
    <div className="coupons-page">
      <ConfirmModal
        isOpen={confirmModal.open}
        title="Delete Coupon"
        message={`Delete coupon "${confirmModal.code}"? This cannot be undone.`}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleDelete}
        onCancel={() => setConfirmModal({ open: false, id: null, code: '' })}
      />

      {/* Header */}
      <div className="page-header">
        <div>
          <h2 className="page-title"><i className="bi bi-tag-fill me-2"></i>{t('promotions.title')}</h2>
          <p className="page-subtitle">{t('promotions.pageSubtitle')}</p>
        </div>
      </div>

      {/* Sales Tax & Discount settings card */}
      <div className="coupon-form-card" style={{ marginBottom: '1.25rem' }}>
        <h5 className="coupon-form-title"><i className="bi bi-percent me-2"></i>{t('promotions.taxDiscountTitle')}</h5>
        <p className="page-subtitle" style={{ marginTop: '-0.4rem', marginBottom: '1rem' }}>{t('promotions.taxDiscountSub')}</p>
        <div className="form-grid-2">
          {/* Sales Tax */}
          <div className="form-group">
            <label className="form-label">{t('promotions.salesTax')}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                className="form-select-input"
                style={{ maxWidth: 130 }}
                value={settings.salesTaxType}
                onChange={e => setSettings(p => ({ ...p, salesTaxType: e.target.value }))}
              >
                <option value="fixed">{t('promotions.fixed')}</option>
                <option value="percentage">{t('promotions.percent')}</option>
              </select>
              <input
                type="number" min="0" step="0.01"
                className="form-input"
                placeholder="0.00"
                value={settings.salesTaxValue}
                onChange={e => setSettings(p => ({ ...p, salesTaxValue: e.target.value }))}
              />
            </div>
          </div>
          {/* Discount */}
          <div className="form-group">
            <label className="form-label">{t('promotions.discount')}</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <select
                className="form-select-input"
                style={{ maxWidth: 130 }}
                value={settings.discountType}
                onChange={e => setSettings(p => ({ ...p, discountType: e.target.value }))}
              >
                <option value="fixed">{t('promotions.fixed')}</option>
                <option value="percentage">{t('promotions.percent')}</option>
              </select>
              <input
                type="number" min="0" step="0.01"
                className="form-input"
                placeholder="0.00"
                value={settings.discountValue}
                onChange={e => setSettings(p => ({ ...p, discountValue: e.target.value }))}
              />
            </div>
          </div>
        </div>
        <div className="form-actions">
          <button className="btn-submit-form" onClick={saveSettings} disabled={savingSettings}>
            {savingSettings ? <><span className="spinner-border spinner-border-sm me-2"></span>{t('common.saving')}</> : <><i className="bi bi-save me-2"></i>{t('promotions.saveSettings')}</>}
          </button>
        </div>
      </div>

      {/* Coupons section header */}
      <div className="promotions-section-header">
        <div>
          <h3 className="promotions-section-title"><i className="bi bi-ticket-perforated-fill me-2"></i>{t('promotions.couponsHeading')}</h3>
          <p className="promotions-section-sub">{coupons.filter(c => c.isActive).length} {t('promotions.subtitle')} · {t('promotions.couponsSub')}</p>
        </div>
        <button onClick={() => { setShowForm(v => !v); setEditId(null); setForm(EMPTY_FORM); }} className="btn-primary-action">
          <i className="bi bi-plus-circle me-2"></i>{showForm ? t('common.cancel') : t('promotions.createCoupon')}
        </button>
      </div>

      {/* Form */}
      <AnimatePresence>
        {showForm && (
          <motion.form
            className="coupon-form-card"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleSubmit}
          >
            <h5 className="coupon-form-title">{editId ? t('promotions.editCoupon') : t('promotions.createNewCoupon')}</h5>
            <div className="form-grid-3">
              <div className="form-group">
                <label className="form-label">{t('promotions.couponCode')} <span className="required">*</span></label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. SAVE20"
                  value={form.code}
                  onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                  style={{ textTransform: 'uppercase', letterSpacing: 2 }}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t('promotions.discountType')}</label>
                <select className="form-select-input" value={form.discountType} onChange={e => setForm(p => ({ ...p, discountType: e.target.value }))}>
                  <option value="percentage">{t('promotions.percentageOption')}</option>
                  <option value="fixed">{t('promotions.fixedOption')}</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">{t('promotions.discountValue')} <span className="required">*</span></label>
                <input
                  type="number" min="0" step="0.01"
                  className="form-input"
                  placeholder={form.discountType === 'percentage' ? 'e.g. 20' : 'e.g. 15.00'}
                  value={form.discountValue}
                  onChange={e => setForm(p => ({ ...p, discountValue: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label className="form-label">{t('promotions.minOrder')}</label>
                <input type="number" min="0" step="0.01" className="form-input" placeholder="0.00" value={form.minOrderAmount} onChange={e => setForm(p => ({ ...p, minOrderAmount: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('promotions.maxUses')}</label>
                <input type="number" min="1" className="form-input" placeholder={t('promotions.unlimited')} value={form.maxUses} onChange={e => setForm(p => ({ ...p, maxUses: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">{t('promotions.expiryDate')}</label>
                <input type="date" className="form-input" value={form.expiresAt} onChange={e => setForm(p => ({ ...p, expiresAt: e.target.value }))} />
              </div>
            </div>
            <div className="form-check-row" style={{ marginBottom: '1rem' }}>
              <input type="checkbox" id="couponActive" className="form-check-input" checked={form.isActive} onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))} />
              <label htmlFor="couponActive" className="form-check-label"><i className="bi bi-toggle-on me-1"></i>{t('common.active')}</label>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => { setShowForm(false); setEditId(null); setForm(EMPTY_FORM); }} className="btn-cancel-form">{t('common.cancel')}</button>
              <button type="submit" className="btn-submit-form" disabled={formLoading}>
                {formLoading ? <><span className="spinner-border spinner-border-sm me-2"></span>{t('common.saving')}</> : <><i className="bi bi-save me-2"></i>{editId ? t('common.update') : t('common.create')}</>}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Coupon List */}
      {loading ? (
        <div className="loading-container">
          <div className="spinner-border" style={{ color: '#a05a2c' }}></div>
          <p className="loading-text">{t('common.loading')}</p>
        </div>
      ) : coupons.length === 0 ? (
        <div className="empty-state">
          <i className="bi bi-tag"></i>
          <p>{t('promotions.noCoupons')}</p>
        </div>
      ) : (
        <div className="coupon-list">
          <AnimatePresence>
            {coupons.map((coupon, i) => (
              <motion.div
                key={coupon._id}
                className={`coupon-item ${!coupon.isActive ? 'inactive' : ''} ${isExpired(coupon) ? 'expired' : ''}`}
                layout
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ delay: i * 0.04 }}
              >
                {/* Code + Copy */}
                <div className="coupon-code-block">
                  <span className="coupon-code">{coupon.code}</span>
                  <button
                    className="btn-copy"
                    onClick={() => copyCode(coupon.code)}
                    title="Copy code"
                  >
                    <i className={`bi ${copiedCode === coupon.code ? 'bi-check-lg' : 'bi-clipboard'}`}></i>
                  </button>
                </div>

                {/* Details */}
                <div className="coupon-details">
                  <span className="coupon-value">
                    {coupon.discountType === 'percentage' ? `${coupon.discountValue}% ${t('promotions.off')}` : `¥${coupon.discountValue} ${t('promotions.off')}`}
                  </span>
                  {coupon.minOrderAmount > 0 && (
                    <span className="coupon-min">{t('promotions.min')}: ¥{coupon.minOrderAmount}</span>
                  )}
                  <span className="coupon-uses">
                    {coupon.usedCount}/{coupon.maxUses === null ? '∞' : coupon.maxUses} {t('promotions.uses')}
                  </span>
                  {coupon.expiresAt && (
                    <span className={`coupon-expiry ${isExpired(coupon) ? 'expired' : ''}`}>
                      <i className="bi bi-calendar me-1"></i>
                      {isExpired(coupon) ? t('promotions.expired') : `${t('promotions.exp')} ${new Date(coupon.expiresAt).toLocaleDateString()}`}
                    </span>
                  )}
                </div>

                {/* Status + Actions */}
                <div className="coupon-actions">
                  <button
                    className={`btn-toggle-active ${coupon.isActive ? 'active' : 'inactive'}`}
                    onClick={() => handleToggleActive(coupon)}
                  >
                    <i className={`bi ${coupon.isActive ? 'bi-toggle-on' : 'bi-toggle-off'} me-1`}></i>
                    {coupon.isActive ? t('common.active') : t('common.inactive')}
                  </button>
                  <button className="btn-edit-icon" onClick={() => handleEdit(coupon)} title="Edit">
                    <i className="bi bi-pencil-square"></i>
                  </button>
                  <button className="btn-delete-icon" onClick={() => setConfirmModal({ open: true, id: coupon._id, code: coupon.code })} title="Delete">
                    <i className="bi bi-trash"></i>
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
